import React from 'react';
import { Question, UserAnswer } from '../types';
import { useDisplayTranslation } from '../hooks/useDisplayTranslation';

interface ReviewCardProps {
    question: Question;
    userAnswer: UserAnswer | undefined;
    displayLanguage: 'pt' | 'en' | 'es';
}

const ReviewCard: React.FC<ReviewCardProps> = ({ question, userAnswer, displayLanguage }) => {
    const { stem, options: displayOptions, explanation: locExpl, loading: trLoading } = useDisplayTranslation(question, displayLanguage);

    const getOptionClass = (optionKey: string) => {
        const isCorrect = question.correctAnswers.includes(optionKey);
        const isSelected = userAnswer?.selected?.includes(optionKey) ?? false;

        if (isCorrect) return 'bg-green-100 dark:bg-green-800/60 border-green-300 dark:border-green-500 ring-2 ring-green-400';
        if (isSelected && !isCorrect) return 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-600 ring-2 ring-red-500';
        
        return 'bg-gray-100 dark:bg-slate-700 border-gray-200 dark:border-slate-600';
    };
    
    const getSelectionIcon = (optionKey: string) => {
        const isSelected = userAnswer?.selected?.includes(optionKey) ?? false;
        if (!isSelected) return null;

        const isCorrect = userAnswer?.isCorrect;

        if (question.isMultiChoice) {
             const isThisOptionCorrect = question.correctAnswers.includes(optionKey);
             return isThisOptionCorrect 
                ? <i className="fas fa-check text-green-400" title={displayLanguage === 'en' ? 'Your correct selection' : displayLanguage === 'es' ? 'Tu selección correcta' : 'Sua seleção correta'}></i> 
                : <i className="fas fa-times text-red-400" title={displayLanguage === 'en' ? 'Your incorrect selection' : displayLanguage === 'es' ? 'Tu selección incorrecta' : 'Sua seleção incorreta'}></i>;
        } else {
             return isCorrect
                ? <i className="fas fa-check-circle text-green-400" title={displayLanguage === 'en' ? 'Your selection (Correct)' : displayLanguage === 'es' ? 'Tu selección (Correcta)' : 'Sua seleção (Correta)'}></i>
                : <i className="fas fa-times-circle text-red-400" title={displayLanguage === 'en' ? 'Your selection (Incorrect)' : displayLanguage === 'es' ? 'Tu selección (Incorrecta)' : 'Sua seleção (Incorreta)'}></i>;
        }
    }

    return (
        <div className="bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-lg shadow-2xl mb-6 relative">
            {trLoading && displayLanguage !== 'pt' && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 dark:bg-slate-900/70 backdrop-blur-[1px]">
                    <span className="text-sm text-gray-600 dark:text-slate-300">
                        {displayLanguage === 'en' ? 'Translating…' : displayLanguage === 'es' ? 'Traduciendo…' : '…'}
                    </span>
                </div>
            )}
            {question.imgLink && (
                <div className="mb-4">
                    <img src={question.imgLink} alt={displayLanguage === 'en' ? `Question image ${question.id}` : displayLanguage === 'es' ? `Imagen de pregunta ${question.id}` : `Imagem da questão ${question.id}`} className="max-h-64 w-auto mx-auto rounded border border-gray-200 dark:border-slate-700" />
                </div>
            )}
            <div
                className="prose prose-lg dark:prose-invert text-gray-700 dark:text-slate-300 mb-6"
                dangerouslySetInnerHTML={{ __html: stem }}
            />
            
            <div className="space-y-3">
                {Object.entries(displayOptions).map(([key, value]) => (
                    <div
                        key={key}
                        className={`p-4 border rounded-lg transition-all ${getOptionClass(key)}`}
                    >
                        <div className="flex items-center">
                            <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center mr-4">
                                {getSelectionIcon(key)}
                            </div>
                            <span className="font-bold mr-2 text-orange-400">{key}.</span>
                            <span dangerouslySetInnerHTML={{ __html: value }} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 p-4 bg-gray-50 dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700">
                <h3 className="text-xl font-bold text-orange-400 mb-3">{displayLanguage === 'en' ? 'Explanation' : displayLanguage === 'es' ? 'Explicación' : 'Explicação'}</h3>
                <p className="text-gray-700 dark:text-slate-300 mb-4">{locExpl.general}</p>
                <div className="space-y-3">
                    {Object.entries(locExpl.options || {}).map(([key, expl]) => {
                            const isCorrect = question.correctAnswers.includes(key);
                            const icon = isCorrect ? <i className="fas fa-check-circle text-green-500 mr-2"></i> : <i className="fas fa-times-circle text-red-500 mr-2"></i>;
                        return (
                            <div key={key} className="text-gray-600 dark:text-slate-400">
                                <p><strong>{displayLanguage === 'en' ? 'Option' : displayLanguage === 'es' ? 'Opción' : 'Opção'} {key}:</strong> {icon} {expl}</p>
                            </div>
                        )
                    })}
                </div>
                 {question.docLink && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                        <h4 className="text-lg font-semibold text-cyan-400 mb-2">{displayLanguage === 'en' ? 'Official Source' : displayLanguage === 'es' ? 'Fuente oficial' : 'Fonte Oficial'}</h4>
                        <a 
                            href={question.docLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                        >
                            {displayLanguage === 'en' ? 'Read documentation' : displayLanguage === 'es' ? 'Leer documentación' : 'Ler a documentação'} <i className="fas fa-external-link-alt ml-1 text-xs"></i>
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewCard;
